import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCourses } from '@/hooks/useCourses';
import { useLessonProgress } from '@/hooks/useLessonProgress';
import { useSubscriptionWithTier } from '@/hooks/useSubscription';
import { useAuth } from '@/lib/auth';
import { CourseCard } from './CourseCard';
import { getCourseProgress, getModuleLessonCounts } from '@/types/courses';
import { Search, BookOpen, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UpgradePrompt } from '@/components/upgrade/UpgradePrompt';
import { createPortal } from 'react-dom';

interface CourseLibraryProps {
  courseType?: 'standard' | 'nextgen';
  basePath: string;
}

export function CourseLibrary({ courseType, basePath }: CourseLibraryProps) {
  const navigate = useNavigate();
  const { user, isAuthResolved } = useAuth();
  const { data: courses, isLoading, error } = useCourses({ courseType });

  // Diagnostic logging — remove after debugging
  useEffect(() => {
    console.log('[CourseLibrary] Mount diagnostics:', {
      courseType,
      isAuthResolved,
      userId: user?.id,
      isLoading,
      hasError: !!error,
      errorMsg: error ? (error as Error).message : null,
      courseCount: courses?.length ?? 'undefined',
    });
  }, [courseType, isAuthResolved, user, isLoading, error, courses]);
  const { data: completedLessons = {} } = useLessonProgress();
  const { isActive, tierName, isLoading: subLoading } = useSubscriptionWithTier();
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);

  const isSubscribed = isActive && tierName !== 'Free';

  const filteredCourses = useMemo(() => {
    if (!courses) return [];
    // Filter out draft/archived
    let filtered = courses.filter(
      (c) =>
        c.status?.toLowerCase() !== 'archived' &&
        c.status?.toLowerCase() !== 'draft'
    );
    // Search filter
    const q = searchQuery.toLowerCase().trim();
    if (q) {
      filtered = filtered.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.tile_description?.toLowerCase().includes(q) ||
          c.overview?.toLowerCase().includes(q)
      );
    }
    // Sort: featured first, then alphabetical
    return [...filtered].sort((a, b) => {
      const aFeatured = a.is_featured && a.status !== 'Coming Soon';
      const bFeatured = b.is_featured && b.status !== 'Coming Soon';
      if (aFeatured && !bFeatured) return -1;
      if (!aFeatured && bFeatured) return 1;

      const aComingSoon = a.status === 'Coming Soon';
      const bComingSoon = b.status === 'Coming Soon';
      if (!aComingSoon && bComingSoon) return -1;
      if (aComingSoon && !bComingSoon) return 1;

      return a.title.localeCompare(b.title);
    });
  }, [courses, searchQuery]);

  const handleSelect = (courseId: string) => {
    if (!isSubscribed && !subLoading) {
      setShowUpgrade(true);
      return;
    }
    navigate(`${basePath}/${courseId}`);
  };

  if (error) {
    return (
      <div className="text-center py-20">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 max-w-lg mx-auto">
          <h3 className="text-lg font-medium text-red-800 dark:text-red-300 mb-2">
            Failed to load courses
          </h3>
          <p className="text-sm text-red-600 dark:text-red-400 font-mono">
            {(error as Error).message || String(error)}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="space-y-2">
            <div className="h-7 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
          <div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Course Library
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Select a subject to begin studying.
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F37022]/20 focus:border-[#F37022]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Grid */}
        {filteredCourses.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                progress={getCourseProgress(course, completedLessons)}
                counts={getModuleLessonCounts(course)}
                isSubscribed={isSubscribed}
                onSelect={handleSelect}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
              {searchQuery ? (
                <Search className="h-8 w-8 text-gray-400" />
              ) : (
                <BookOpen className="h-8 w-8 text-gray-400" />
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {searchQuery ? 'No courses found' : 'No Courses Yet'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {searchQuery
                ? 'Try searching for a different keyword.'
                : 'Courses are being prepared. Check back soon.'}
            </p>
            {searchQuery && (
              <Button
                variant="link"
                onClick={() => setSearchQuery('')}
                className="mt-2 text-[#F37022]"
              >
                Clear search
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Upgrade prompt modal for free tier users */}
      {showUpgrade &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[10000]"
            onClick={() => setShowUpgrade(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <UpgradePrompt
                feature="courses"
                variant="modal"
                onUpgradeClick={() => {
                  setShowUpgrade(false);
                  navigate('/pricing');
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
